import { FC, useState, useEffect } from "react";
import {
  Label,
  Input,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  RadioGroup,
  RadioGroupItem,
} from "@capacit-com/lara-law-shared-ui";
import { Trash2, Plus, Pencil, ExternalLink, Link } from "lucide-react";
import { ShortcutWidgetConfig, ShortcutItem, getFaviconUrl } from "@/models/dashboard/WidgetConfig";
import { uuidv4 } from "@capacit-com/lara-law-shared-ui/uuid";

interface ShortcutConfigFormProps {
  config: ShortcutWidgetConfig;
  onChange: (config: ShortcutWidgetConfig) => void;
  onDraftStateChange?: (hasDraft: boolean) => void;
}

interface EditFormState {
  name: string;
  url: string;
}

const ShortcutConfigForm: FC<ShortcutConfigFormProps> = ({
  config,
  onChange,
  onDraftStateChange,
}) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({ name: "", url: "" });
  const notifyDraftState = (hasDraft: boolean) => onDraftStateChange?.(hasDraft);

  useEffect(() => {
    const hasDraft = isAddingNew || editingId !== null;
    onDraftStateChange?.(hasDraft);
  }, [isAddingNew, editingId, onDraftStateChange]);

  const updateConfig = (updates: Partial<ShortcutWidgetConfig>) => {
    onChange({ ...config, ...updates });
  };

  const isExternalUrl = (url: string) => {
    return url.startsWith("http://") || url.startsWith("https://");
  };

  const startAddNew = () => {
    notifyDraftState(true);
    setIsAddingNew(true);
    setEditForm({ name: "", url: "" });
  };

  const startEditing = (shortcut: ShortcutItem) => {
    notifyDraftState(true);
    setEditingId(shortcut.id);
    setEditForm({
      name: shortcut.name,
      url: shortcut.url,
    });
  };

  const cancelEdit = () => {
    notifyDraftState(false);
    setIsAddingNew(false);
    setEditingId(null);
    setEditForm({ name: "", url: "" });
  };

  const saveShortcut = () => {
    if (!editForm.name.trim() || !editForm.url.trim()) return;

    const url = editForm.url.trim();
    const shortcut: ShortcutItem = {
      id: editingId || uuidv4(),
      name: editForm.name.trim(),
      url,
      faviconUrl: getFaviconUrl(url),
    };

    if (isAddingNew) {
      updateConfig({ shortcuts: [...config.shortcuts, shortcut] });
    } else if (editingId) {
      updateConfig({
        shortcuts: config.shortcuts.map((s) =>
          s.id === editingId ? shortcut : s
        ),
      });
    }

    cancelEdit();
  };

  const removeShortcut = (id: string) => {
    updateConfig({
      shortcuts: config.shortcuts.filter((s) => s.id !== id),
    });
  };

  const renderEditForm = () => {
    const previewFaviconUrl = getFaviconUrl(editForm.url);
    
    return (
      <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
        <div className="space-y-2">
          <Label>Navn</Label>
          <Input
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            placeholder="fx. Google, Retsinformation"
          />
        </div>

        <div className="space-y-2">
          <Label>URL</Label>
          <div className="flex items-center gap-2">
            <Input
              value={editForm.url}
              onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
              placeholder="fx. https://google.com eller /workspaces"
              className="flex-1"
            />
            {previewFaviconUrl && (
              <div className="flex items-center justify-center w-8 h-8 border rounded bg-background">
                <img 
                  src={previewFaviconUrl} 
                  alt="Favicon preview" 
                  className="h-5 w-5"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Eksterne links (https://...) åbnes i ny fane. Interne links (/...) navigerer i appen.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={cancelEdit}>
            Annuller
          </Button>
          <Button
            size="sm"
            onClick={saveShortcut}
            disabled={!editForm.name.trim() || !editForm.url.trim()}
          >
            {isAddingNew ? "Tilføj" : "Gem"}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Layout */}
      <div className="space-y-2">
        <Label>Layout</Label>
        <RadioGroup
          value={config.layout || "grid"}
          onValueChange={(value: "grid" | "list") =>
            updateConfig({ layout: value })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="grid" id="layout-grid" />
            <Label htmlFor="layout-grid" className="font-normal">
              Gitter
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="list" id="layout-list" />
            <Label htmlFor="layout-list" className="font-normal">
              Liste
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Shortcuts List */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Genveje</Label>
          {!isAddingNew && !editingId && (
            <Button variant="outline" size="sm" onClick={startAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Tilføj genvej
            </Button>
          )}
        </div>

        {(isAddingNew || editingId) && renderEditForm()}

        {config.shortcuts.length > 0 && !isAddingNew && !editingId && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Navn</TableHead>
                <TableHead>URL</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {config.shortcuts.map((shortcut) => (
                <TableRow key={shortcut.id}>
                  <TableCell>
                    {shortcut.faviconUrl ? (
                      <img 
                        src={shortcut.faviconUrl} 
                        alt="" 
                        className="h-4 w-4"
                        onError={(e) => {
                          // Hide broken image, parent will show fallback styling
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : isExternalUrl(shortcut.url) ? (
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Link className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{shortcut.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm truncate max-w-[200px]">
                    {shortcut.url}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => startEditing(shortcut)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => removeShortcut(shortcut.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {config.shortcuts.length === 0 && !isAddingNew && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Ingen genveje tilføjet endnu.
          </p>
        )}
      </div>
    </div>
  );
};

export default ShortcutConfigForm;
